import { AbstractControl, ValidationErrors } from '@angular/forms';
import { addDays, format, parse } from 'date-fns';

/**
 * Formats a date to US format (MM/dd/yyyy) - Backend format
 * @param date - Date string or Date object
 * @returns Date in MM/dd/yyyy format
 */
export const formatToUSDate = (date: string | Date): string => {
  if (!date) return '';
  try {
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) return '';
    return format(parsedDate, 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

/**
 * Formats a date to US datetime format (MM/dd/yyyy hh:mm AM/PM)
 * @param date - Date string or Date object
 * @returns Date & time in locale US format or '' if invalid
 */
export const formatToUSDateTime = (date: string | Date): string => {
  if (!date) return '';
  try {
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) return '';

    return parsedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

/**
 * Converts backend format (MM/dd/yyyy) to HTML5 input format (yyyy-MM-dd)
 * This is used when receiving data from backend to display in date picker
 * @param dateStr - Date string in MM/dd/yyyy format
 * @returns Date in yyyy-MM-dd format for HTML5 input
 */
export const backendToInputFormat = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    // Parse the MM/dd/yyyy format from backend
    const parsedDate = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (isNaN(parsedDate.getTime())) return '';
    // Convert to yyyy-MM-dd for HTML5 input
    return format(parsedDate, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Converts HTML5 input format (yyyy-MM-dd) to backend format (MM/dd/yyyy)
 * This is used when sending data from date picker to backend
 * @param dateStr - Date string in yyyy-MM-dd format
 * @returns Date in MM/dd/yyyy format for backend
 */
export const inputToBackendFormat = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    // Parse the yyyy-MM-dd format from HTML5 input
    const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    if (isNaN(parsedDate.getTime())) return '';
    // Convert to MM/dd/yyyy for backend
    return format(parsedDate, 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

/**
 * Converts any date string or Date object to the local browser date format.
 * The output format is determined by the user's browser locale.
 * Returns an empty string if the input is invalid or cannot be parsed.
 *
 * @param date - The date to format (string or Date)
 * @returns The date formatted in the local browser format, or '' if invalid
 */
export const formatToDayMonthFormat = (date: string | Date): string => {
  if (!date) return '';
  try {
    let parsedDate: Date;

    if (date instanceof Date) {
      parsedDate = date;
    } else {
      parsedDate = new Date(date);
      parsedDate.setHours(0, 0, 0, 0);
    }

    if (isNaN(parsedDate.getTime())) {
      return '';
    }

    // We can use the en-GB locale to get the date in the format of day/month/year as it is the available format on that country
    return parsedDate.toLocaleDateString('en-GB');
  } catch {
    return '';
  }
};

/**
 * Gets today's date in yyyy-MM-dd format
 * @returns Today's date as a string
 */
export const getToday = (): string => {
  return format(new Date(), 'yyyy-MM-dd');
};

/**
 * Gets tomorrow's date in yyyy-MM-dd format
 * @returns Tomorrow's date as a string
 */
export const getTomorrow = (): string => {
  return format(addDays(new Date(), 1), 'yyyy-MM-dd');
};

/**
 * Converts a date string from MM/dd/yyyy to yyyy-MM-dd
 * @param date - Date string in MM/dd/yyyy format
 * @returns Date in yyyy-MM-dd format
 */
export const convertToUSDate = (date: string): string => {
  if (!date) return '';
  try {
    const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
    if (isNaN(parsedDate.getTime())) return '';
    return format(parsedDate, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Validates that the from date is before the to date
 * @param control - The form control to validate
 * @returns Validation errors or null
 */
export const createDateRangeValidator =
  (fromName = 'from_date', toName = 'to_date') =>
  (control: AbstractControl): ValidationErrors | null => {
    const fromDate = control.get(fromName)?.value;
    const toDate = control.get(toName)?.value;

    if (fromDate && toDate) {
      const parsedFrom = parse(fromDate, 'MM/dd/yyyy', new Date());
      const parsedTo = parse(toDate, 'MM/dd/yyyy', new Date());

      if (parsedFrom > parsedTo) {
        return { invalidDateRange: true };
      }
    }

    return null;
  };

export const dateControlRangeValidator = (control: AbstractControl) =>
  createDateRangeValidator()(control);
